/* HPN NAD3 Extra Strength - Buy Box (scoped, no globals) */
(() => {
  const SELECTOR_ROOT = '[data-hpn-nad3-extra-strength-buy-box]';
  const SELECTOR_CTA = '[data-hpn-nad3-extra-strength-buy-box-cta]';
  const SELECTOR_STICKY = '[data-hpn-nad3-extra-strength-buy-box-sticky]';
  const SELECTOR_STICKY_CTA = '[data-hpn-nad3-extra-strength-buy-box-sticky-cta]';
  const CHECKOUT_REDIRECT_FLAG = '__hpnNad3ExtraStrengthBuyBoxCheckoutRedirectUntil';

  function parseDiscountCodes(raw) {
    const text = (raw || '').trim();
    if (!text) return [];
    // Accept comma or newline separated
    return text
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  async function postJSON(url, bodyObj) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addItemsBatch(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items || [] }),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addSingleItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(item || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector('[data-hpn-nad3-extra-strength-buy-box-status]');
    if (!el) return;
    el.textContent = msg || '';
  }

  function getScrollOffsetPx() {
    return 0;
  }

  function smoothScrollToTarget(targetEl) {
    if (!targetEl) return;

    const offset = getScrollOffsetPx();
    const rect = targetEl.getBoundingClientRect();
    const y = Math.round(window.pageYOffset + rect.top - offset - 12); // 12px breathing room

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  }

  function bindDynamicKickerLocation(root) {
    const kicker = root.querySelector(
      '[data-hpn-nad3-extra-strength-buy-box-dynamic-kicker]'
    );
    if (!kicker || kicker.__hpnNad3ExtraStrengthDynamicKickerBound) return;

    const baseText = (
      kicker.getAttribute(
        'data-hpn-nad3-extra-strength-buy-box-kicker-base'
      ) || kicker.textContent || ''
    ).trim();
    const locationTemplate = (
      kicker.getAttribute(
        'data-hpn-nad3-extra-strength-buy-box-location-template'
      ) || ''
    ).trim();

    if (
      !baseText ||
      !locationTemplate ||
      !/\[location\]/i.test(locationTemplate)
    ) {
      return;
    }

    kicker.__hpnNad3ExtraStrengthDynamicKickerBound = true;

    const escapeRegExp = (value) => {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const templatePattern = locationTemplate
      .split(/(\[location\]|\[date\])/gi)
      .map((part) => {
        if (/^\[location\]$/i.test(part)) return '(.+?)';
        if (/^\[date\]$/i.test(part)) return '.+?';

        return escapeRegExp(part).replace(/\s+/g, '\\s+');
      })
      .join('');

    const locationMatcher = new RegExp(
      `^\\s*${templatePattern}\\s*$`,
      'i'
    );

    const extractLocation = (messageText) => {
      const normalizedText = (messageText || '')
        .replace(/\s+/g, ' ')
        .trim();

      if (!normalizedText) return '';

      const templateMatch = normalizedText.match(locationMatcher);
      let location =
        templateMatch && templateMatch[1]
          ? templateMatch[1].trim()
          : '';

      if (!location) {
        const fallbackMatch = normalizedText.match(
          /\bto\s+(.+?)(?=[!?.](?:\s|$)|$)/i
        );

        location =
          fallbackMatch && fallbackMatch[1]
            ? fallbackMatch[1].trim()
            : '';
      }

      location = location
        .replace(/\s+/g, ' ')
        .replace(/[!?.]+$/g, '')
        .trim();

      if (!location || /\[location\]/i.test(location)) return '';

      return location;
    };

    const updateKicker = () => {
      const locationTextElements = root.querySelectorAll(
        '.hpn-nad3-extra-strength-buy-box__offerLocationMessage .location-message__text, ' +
          '.hpn-nad3-extra-strength-buy-box__stickyLocationMessage .location-message__text'
      );

      let location = '';

      locationTextElements.forEach((messageElement) => {
        if (location) return;
        location = extractLocation(messageElement.textContent);
      });

      if (!location) {
        kicker.textContent = baseText;
        return;
      }

      const normalizedBaseText = baseText
        .replace(/\s*[!?.]+\s*$/g, '')
        .trim();

      kicker.textContent = `${normalizedBaseText} TO ${location}!`;
    };

    const locationMessageContainers = root.querySelectorAll(
      '.hpn-nad3-extra-strength-buy-box__offerLocationMessage, ' +
        '.hpn-nad3-extra-strength-buy-box__stickyLocationMessage'
    );

    if (!locationMessageContainers.length) return;

    const observer = new MutationObserver(updateKicker);

    locationMessageContainers.forEach((container) => {
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    });

    updateKicker();
  }

  function bindInfoDropdowns(root) {
    const items = root.querySelectorAll('.hpn-nad3-extra-strength-buy-box__infoItem');
    if (!items || !items.length) return;

    const pauseInfoVideos = (scope) => {
      if (!scope) return;

      const videos = scope.querySelectorAll(
        '.hpn-nad3-extra-strength-buy-box__infoMediaVideo'
      );

      videos.forEach((video) => {
        video.pause();
      });
    };

    const playInfoVideo = (scope) => {
      if (!scope) return;

      const video = scope.querySelector(
        '.hpn-nad3-extra-strength-buy-box__infoMediaVideo'
      );
      if (!video) return;

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    items.forEach((item) => {
      const btn = item.querySelector('[data-hpn-nad3-extra-strength-buy-box-info-btn]');
      const panel = item.querySelector('[data-hpn-nad3-extra-strength-buy-box-info-panel]');
      if (!btn || !panel) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all siblings (accordion behavior)
        items.forEach((sib) => {
          if (sib === item) return;

          sib.classList.remove('is-open');

          const sibBtn = sib.querySelector('[data-hpn-nad3-extra-strength-buy-box-info-btn]');
          const sibPanel = sib.querySelector('[data-hpn-nad3-extra-strength-buy-box-info-panel]');

          if (sibBtn) sibBtn.setAttribute('aria-expanded', 'false');

          if (sibPanel) {
            pauseInfoVideos(sibPanel);
            sibPanel.hidden = true;
          }
        });

        if (isOpen) {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          pauseInfoVideos(panel);
          panel.hidden = true;
        } else {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          playInfoVideo(panel);
        }
      });
    });
  }

  function bindRteScrollLinks(root) {
    // Only handle anchor links inside this section’s RTE areas
    const rteContainers = root.querySelectorAll(
      '.hpn-nad3-extra-strength-buy-box__offerBody.rte'
    );
    if (!rteContainers || !rteContainers.length) return;

    rteContainers.forEach((wrap) => {
      wrap.addEventListener('click', (e) => {
        const a = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!a) return;

        const href = (a.getAttribute('href') || '').trim();
        if (!href || href === '#') return;

        // Only intercept same-page hash links (e.g. #reviews)
        if (href.charAt(0) !== '#') return;

        const id = href.slice(1);
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        e.preventDefault();
        smoothScrollToTarget(target);

        // Optional: keep URL hash in sync without jumping
        try {
          history.pushState(null, '', `#${id}`);
        } catch (err) {}
      });
    });
  }

  function setStickyVisible(root, isVisible) {
    if (!root) return;

    const sticky = root.querySelector(SELECTOR_STICKY);
    if (!sticky) return;

    if (isVisible) {
      root.classList.add('hpn-nad3-extra-strength-buy-box-sticky-active');
      sticky.setAttribute('aria-hidden', 'false');
    } else {
      root.classList.remove('hpn-nad3-extra-strength-buy-box-sticky-active');
      sticky.setAttribute('aria-hidden', 'true');
    }
  }

  /** When multiple buy box sections want the sticky bar, pick one winner. */
  function pickStickyWinner(roots) {
    if (!roots || !roots.length) return null;
    if (roots.length === 1) return roots[0];

    const vh = window.innerHeight || 0;
    let best = null;
    let bestScore = -1;

    for (const root of roots) {
      const r = root.getBoundingClientRect();
      const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      const score = Math.max(0, visible);
      if (score > bestScore) {
        bestScore = score;
        best = root;
      }
    }

    if (bestScore > 0) return best;
    return roots[roots.length - 1];
  }

  let stickyGlobalRaf = null;

  function applyGlobalSticky() {
    const roots = Array.from(document.querySelectorAll(SELECTOR_ROOT));
    const want = roots.filter(
      (r) => r.__hpnNad3ExtraStrengthBuyBoxStickyWantsOn && r.querySelector(SELECTOR_STICKY)
    );

    let winner = null;
    if (want.length === 1) winner = want[0];
    else if (want.length > 1) winner = pickStickyWinner(want);

    roots.forEach((r) => {
      if (!r.querySelector(SELECTOR_STICKY)) return;
      setStickyVisible(r, winner !== null && r === winner);
    });
  }

  function scheduleApplyGlobalSticky() {
    if (stickyGlobalRaf != null) return;
    stickyGlobalRaf = requestAnimationFrame(() => {
      stickyGlobalRaf = null;
      applyGlobalSticky();
    });
  }

  function ensureGlobalStickyScrollSync() {
    if (window.__hpnNad3ExtraStrengthBuyBoxStickyGlobalScrollBound) return;
    window.__hpnNad3ExtraStrengthBuyBoxStickyGlobalScrollBound = true;
    window.addEventListener('scroll', scheduleApplyGlobalSticky, { passive: true });
    window.addEventListener('resize', scheduleApplyGlobalSticky);
  }

  function setStickyWant(root, wantsSticky) {
    if (!root) return;
    root.__hpnNad3ExtraStrengthBuyBoxStickyWantsOn = !!wantsSticky;
    applyGlobalSticky();
  }

  function bindStickyObserver(root, mainCta) {
    if (!root || !mainCta) return;

    // Avoid double-binding scroll tracking
    if (root.__hpnNad3ExtraStrengthBuyBoxStickyObserved) return;
    root.__hpnNad3ExtraStrengthBuyBoxStickyObserved = true;

    ensureGlobalStickyScrollSync();

    const alwaysVisible =
      root.getAttribute(
        'data-hpn-nad3-extra-strength-buy-box-sticky-always-visible'
      ) === '1';
    const onlyBelowBuyBox =
      root.getAttribute(
        'data-hpn-nad3-extra-strength-buy-box-sticky-only-below'
      ) === '1';

    if (alwaysVisible) {
      setStickyWant(root, true);
      return;
    }

    let stickyPositionRaf = null;

    const updateStickyWant = () => {
      stickyPositionRaf = null;

      if (onlyBelowBuyBox) {
        const rootRect = root.getBoundingClientRect();
        setStickyWant(root, rootRect.bottom <= 0);
        return;
      }

      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        0;
      const ctaRect = mainCta.getBoundingClientRect();
      const visibleTop = Math.max(ctaRect.top, 0);
      const visibleBottom = Math.min(
        ctaRect.bottom,
        viewportHeight
      );
      const visibleHeight = Math.max(
        0,
        visibleBottom - visibleTop
      );
      const ctaHeight = Math.max(
        1,
        ctaRect.height || ctaRect.bottom - ctaRect.top
      );
      const intersectionRatio = visibleHeight / ctaHeight;
      const inView =
        visibleHeight > 0 && intersectionRatio >= 0.25;

      setStickyWant(root, !inView);
    };

    const scheduleStickyPositionUpdate = () => {
      if (stickyPositionRaf != null) return;

      stickyPositionRaf =
        window.requestAnimationFrame(updateStickyWant);
    };

    window.addEventListener(
      'scroll',
      scheduleStickyPositionUpdate,
      { passive: true }
    );
    window.addEventListener(
      'resize',
      scheduleStickyPositionUpdate
    );

    updateStickyWant();
  }

  function setBusy(btn, isBusy) {
    if (!btn) return;
    btn.disabled = !!isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) btn.classList.add('is-busy');
    else btn.classList.remove('is-busy');
  }

  function setCheckoutRedirectGuard(durationMs = 6000) {
    window[CHECKOUT_REDIRECT_FLAG] = Date.now() + Math.max(1000, Number(durationMs) || 6000);
  }

  function clearCheckoutRedirectGuard() {
    delete window[CHECKOUT_REDIRECT_FLAG];
  }

  const BOGO_RELOAD_MARKER = '_hpn_nad3_bogo_reload';
  let bogoReloadStateLoaded = false;
  let bogoReloadStateCache = null;

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';

    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.includes('://')) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function isInternalBogoParam(key) {
    return key === BOGO_RELOAD_MARKER;
  }

  function getBogoConfig(root) {
    if (!root) {
      return {
        enabled: false,
        params: '',
        checkoutUrl: '/checkout',
      };
    }

    return {
      enabled:
        root.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-bogo-url-params-enabled'
        ) === '1',
      params:
        root.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-bogo-url-params'
        ) || '',
      checkoutUrl:
        root.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-checkout-url'
        ) || '/checkout',
    };
  }

  function getConfiguredBogoKeys(root) {
    const keys = new Set();
    if (!root) return keys;

    const rawParams =
      root.getAttribute(
        'data-hpn-nad3-extra-strength-buy-box-bogo-url-params'
      ) || '';

    const params = new URLSearchParams(
      normalizeBogoParamString(rawParams)
    );

    params.forEach((value, key) => {
      if (key && !isInternalBogoParam(key)) {
        keys.add(key);
      }
    });

    return keys;
  }

  function removeInactiveBogoParams(root, effectiveParams) {
    if (!root) return;

    const configuredKeys = getConfiguredBogoKeys(root);
    if (!configuredKeys.size) return;

    const effective = new URLSearchParams(
      normalizeBogoParamString(effectiveParams)
    );
    const url = new URL(window.location.href);
    let changed = false;

    configuredKeys.forEach((key) => {
      if (!effective.has(key) && url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      const relative = url.pathname + (url.search || '') + url.hash;
      window.history.replaceState({}, '', relative);
    }
  }

  function configuredBogoParamsMatchUrl(params) {
    const incoming = new URLSearchParams(
      normalizeBogoParamString(params)
    );
    const current = new URLSearchParams(window.location.search);
    let matches = true;

    incoming.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;
      if (current.get(key) !== value) matches = false;
    });

    return matches;
  }

  function stripInternalBogoParamsFromCurrentUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(BOGO_RELOAD_MARKER)) return;

    url.searchParams.delete(BOGO_RELOAD_MARKER);
    const relative = url.pathname + (url.search || '') + url.hash;
    window.history.replaceState({}, '', relative);
  }

  function getBogoReloadState() {
    if (bogoReloadStateLoaded) return bogoReloadStateCache;

    bogoReloadStateLoaded = true;

    const url = new URL(window.location.href);
    const raw = url.searchParams.get(BOGO_RELOAD_MARKER);

    if (!raw || raw === '1') {
      bogoReloadStateCache = null;
      return bogoReloadStateCache;
    }

    try {
      const parsed = JSON.parse(raw);
      bogoReloadStateCache =
        parsed && typeof parsed === 'object' ? parsed : null;
    } catch (err) {
      bogoReloadStateCache = null;
    }

    return bogoReloadStateCache;
  }

  function clearBogoReloadStateCache() {
    bogoReloadStateCache = null;
  }

  function restoreBogoReloadScroll(state) {
    if (!state) return;

    const scrollX = Number(state.scrollX);
    const scrollY = Number(state.scrollY);

    if (!Number.isFinite(scrollX) || !Number.isFinite(scrollY)) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const html = document.documentElement;
    const previousScrollBehavior = html.style.scrollBehavior;

    html.style.scrollBehavior = 'auto';

    const restore = () => {
      window.scrollTo(
        Math.max(0, scrollX),
        Math.max(0, scrollY)
      );
    };

    restore();

    window.requestAnimationFrame(() => {
      restore();

      window.requestAnimationFrame(() => {
        restore();
      });
    });

    window.setTimeout(() => {
      restore();
      html.style.scrollBehavior = previousScrollBehavior;
    }, 120);
  }

  function reloadForBogoCheckout(root, triggerButton) {
    if (!root) return false;

    const config = getBogoConfig(root);
    const params = config.enabled
      ? normalizeBogoParamString(config.params)
      : '';
    const effective = new URLSearchParams(params);
    const current = new URLSearchParams(window.location.search);
    const configuredKeys = getConfiguredBogoKeys(root);
    let needsReload = false;

    configuredKeys.forEach((key) => {
      const effectiveValue = effective.get(key);
      const currentValue = current.get(key);

      if (effectiveValue === null) {
        if (current.has(key)) needsReload = true;
        return;
      }

      if (currentValue !== effectiveValue) needsReload = true;
    });

    if (!needsReload) return false;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const mainCta = root.querySelector(SELECTOR_CTA);
    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);

    setBusy(mainCta, true);
    setBusy(stickyCta, true);
    setCheckoutRedirectGuard();

    const mergedUrl = new URL(window.location.href);

    configuredKeys.forEach((key) => {
      mergedUrl.searchParams.delete(key);
    });

    effective.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;
      mergedUrl.searchParams.set(key, value);
    });

    mergedUrl.searchParams.set(
      BOGO_RELOAD_MARKER,
      JSON.stringify({
        mode: 'checkout',
        trigger:
          triggerButton && triggerButton.matches(SELECTOR_STICKY_CTA)
            ? 'sticky'
            : 'main',
        sectionId:
          root.getAttribute(
            'data-hpn-nad3-extra-strength-buy-box-section-id'
          ) || '',
        scrollX: Math.round(
          window.scrollX || window.pageXOffset || 0
        ),
        scrollY: Math.round(
          window.scrollY || window.pageYOffset || 0
        ),
      })
    );

    window.location.replace(mergedUrl.toString());
    return true;
  }

  function applyBogoUrlParams(root) {
    if (!root) return false;

    const config = getBogoConfig(root);
    const params = config.enabled
      ? normalizeBogoParamString(config.params)
      : '';

    removeInactiveBogoParams(root, params);

    if (!config.enabled || !params) {
      stripInternalBogoParamsFromCurrentUrl();
      return false;
    }

    if (configuredBogoParamsMatchUrl(params)) {
      stripInternalBogoParamsFromCurrentUrl();
      return false;
    }

    const url = new URL(window.location.href);
    const incoming = new URLSearchParams(params);
    let changed = false;

    incoming.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      if (url.searchParams.get(key) !== value) {
        url.searchParams.set(key, value);
        changed = true;
      }
    });

    url.searchParams.delete(BOGO_RELOAD_MARKER);

    if (changed) {
      const relative = url.pathname + (url.search || '') + url.hash;
      window.history.replaceState({}, '', relative);
    }

    return changed;
  }

  function extractBogoCartAttributes(root) {
    const config = getBogoConfig(root);
    if (!config.enabled) return null;

    const params = normalizeBogoParamString(config.params);
    if (!params) return null;

    const attributes = {};
    let hasAny = false;

    new URLSearchParams(params).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;
      attributes[key] = value;
      hasAny = true;
    });

    return hasAny ? attributes : null;
  }

  function copyPublicSearchParams(sourceParams, targetUrl) {
    sourceParams.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;
      targetUrl.searchParams.set(key, value);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, root) {
    const config = getBogoConfig(root);
    if (!config.enabled) return;

    const params = normalizeBogoParamString(config.params);
    if (!params) return;

    new URLSearchParams(params).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;
      checkoutUrl.searchParams.set(key, value);
    });
  }

  function buildCheckoutUrl(root, discountCodes) {
    const config = getBogoConfig(root);
    const checkoutUrl = new URL(
      config.checkoutUrl || '/checkout',
      window.location.origin
    );

    copyPublicSearchParams(
      new URLSearchParams(window.location.search),
      checkoutUrl
    );

    mergeConfiguredBogoParams(checkoutUrl, root);

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }

    return checkoutUrl.toString();
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart (${response.status})`);
    }

    return response.json();
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      const interval = 200;
      const maxWait = 6000;
      let elapsed = 0;

      const check = async () => {
        try {
          const cart = await fetchCart();

          if (
            cart &&
            typeof cart.item_count === 'number' &&
            cart.item_count > knownItemCount
          ) {
            resolve(true);
            return;
          }
        } catch (err) {
          resolve(false);
          return;
        }

        elapsed += interval;

        if (elapsed >= maxWait) {
          resolve(false);
          return;
        }

        window.setTimeout(check, interval);
      };

      window.setTimeout(check, interval);
    });
  }

  async function handleCheckoutClick(btn) {
    const root = btn && btn.closest(SELECTOR_ROOT);
    if (!root || root.__hpnNad3ExtraStrengthBuyBoxCheckoutSubmitting) return;

    if (reloadForBogoCheckout(root, btn)) return;

    root.__hpnNad3ExtraStrengthBuyBoxCheckoutSubmitting = true;

    const mainCta = root.querySelector(SELECTOR_CTA);
    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);

    setStatus(root, '');
    setBusy(mainCta, true);
    setBusy(stickyCta, true);
    setCheckoutRedirectGuard();

    try {
      applyBogoUrlParams(root);

      const clearCart =
        btn.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-clear-cart'
        ) === '1';
      const mainVariantIdRaw = (
        btn.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-main-variant'
        ) || ''
      ).trim();
      const mainQty = Number(
        btn.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-main-qty'
        ) || 1
      );
      const mainSellingPlanRaw = (
        btn.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-main-selling-plan'
        ) || ''
      ).trim();

      const mainVariantId = Number(mainVariantIdRaw || 0);
      const mainSellingPlanId = Number(mainSellingPlanRaw || 0);

      if (!mainVariantId) {
        throw new Error(
          'Missing main variant ID. Please set it in the section settings.'
        );
      }

      let freebies = [];
      const freebiesRaw =
        btn.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-freebies'
        ) || '[]';

      try {
        freebies = JSON.parse(freebiesRaw) || [];
      } catch (err) {
        freebies = [];
      }

      const mainItem = {
        id: mainVariantId,
        quantity: Math.max(1, mainQty || 1),
      };

      if (mainSellingPlanRaw && mainSellingPlanId > 0) {
        mainItem.selling_plan = mainSellingPlanId;
      }

      const freebieItems = (freebies || [])
        .filter((item) => item && Number(item.id) > 0)
        .map((item) => ({
          id: Number(item.id),
          quantity: Math.max(1, Number(item.quantity) || 1),
        }));

      const discountCodes = parseDiscountCodes(
        btn.getAttribute(
          'data-hpn-nad3-extra-strength-buy-box-discounts'
        )
      );
      const bogoAttributes = extractBogoCartAttributes(root);

      if (clearCart) {
        await postJSON('/cart/clear.js', {});
        await wait(180);
      }

      let cartBeforeAdd = null;
      try {
        cartBeforeAdd = await fetchCart();
      } catch (err) {}

      await addSingleItem(mainItem);

      let addedQuantity = Math.max(1, Number(mainItem.quantity) || 1);

      if (freebieItems.length) {
        try {
          await addItemsBatch(freebieItems);
          addedQuantity += freebieItems.reduce(
            (total, item) =>
              total + Math.max(1, Number(item.quantity) || 1),
            0
          );
        } catch (freebieErr) {
          console.warn(
            'HPN NAD3 Extra Strength freebies failed to add:',
            freebieErr
          );
        }
      }

      let cartAfterAdd = null;
      try {
        cartAfterAdd = await fetchCart();
      } catch (err) {}

      let cartAfterAttributeUpdate = null;
      if (bogoAttributes) {
        cartAfterAttributeUpdate = await postJSON('/cart/update.js', {
          attributes: bogoAttributes,
        });
      }

      if (bogoAttributes) {
        const latestCart = cartAfterAttributeUpdate || cartAfterAdd;
        const knownItemCount =
          latestCart && typeof latestCart.item_count === 'number'
            ? latestCart.item_count
            : null;
        const baselineItemCount =
          cartBeforeAdd && typeof cartBeforeAdd.item_count === 'number'
            ? cartBeforeAdd.item_count
            : null;
        const giftAlreadyPresent =
          knownItemCount !== null &&
          baselineItemCount !== null &&
          knownItemCount > baselineItemCount + addedQuantity;

        if (!giftAlreadyPresent && knownItemCount !== null) {
          await waitForBogoGift(knownItemCount);
        }
      }

      applyBogoUrlParams(root);

      const checkoutUrl = buildCheckoutUrl(root, discountCodes);
      window.location.href = checkoutUrl;
      return;
    } catch (err) {
      const msg =
        err && err.message
          ? err.message
          : 'Unable to start checkout. Please try again.';

      setStatus(root, msg);
      setBusy(mainCta, false);
      setBusy(stickyCta, false);
      root.__hpnNad3ExtraStrengthBuyBoxCheckoutSubmitting = false;
      clearCheckoutRedirectGuard();
    }
  }

  function bindRoot(root) {
    if (!root || root.__hpnNad3ExtraStrengthBuyBoxBound) return;

    const reloadState = getBogoReloadState();
    const rootSectionId =
      root.getAttribute(
        'data-hpn-nad3-extra-strength-buy-box-section-id'
      ) || '';
    const reloadMatchesRoot =
      !!reloadState &&
      (!reloadState.sectionId || reloadState.sectionId === rootSectionId);
    const shouldResumeCheckout =
      reloadMatchesRoot && reloadState.mode === 'checkout';

    stripInternalBogoParamsFromCurrentUrl();

    root.__hpnNad3ExtraStrengthBuyBoxBound = true;

    const cta = root.querySelector(SELECTOR_CTA);
    if (cta) {
      cta.addEventListener('click', () => handleCheckoutClick(cta));
    }

    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);
    if (stickyCta) {
      stickyCta.addEventListener('click', () => handleCheckoutClick(stickyCta));
    }

    if (shouldResumeCheckout) {
      restoreBogoReloadScroll(reloadState);

      const resumeButton =
        reloadState.trigger === 'sticky'
          ? stickyCta || cta
          : cta || stickyCta;

      if (resumeButton) {
        setBusy(cta, true);
        setBusy(stickyCta, true);
        setCheckoutRedirectGuard();

        window.requestAnimationFrame(() => {
          restoreBogoReloadScroll(reloadState);

          window.setTimeout(() => {
            if (!root.__hpnNad3ExtraStrengthBuyBoxCheckoutSubmitting) {
              handleCheckoutClick(resumeButton);
            }
          }, 30);
        });
      }

      clearBogoReloadStateCache();
    }

    // Show sticky only when this section renders the bar (e.g. sticky disabled in theme editor)
    if (cta && root.querySelector(SELECTOR_STICKY)) {
      bindStickyObserver(root, cta);
    }

    // Keep the kicker connected to the detected location message.
    bindDynamicKickerLocation(root);

    // Dropdown info accordions (desktop + mobile placements)
    bindInfoDropdowns(root);

    // Smooth-scroll for hash links inside offer rich text.
    bindRteScrollLinks(root);

    // Gallery: main image switch + thumb strip with side arrows
    const gallery = root.querySelector('[data-hpn-nad3-extra-strength-buy-box-gallery]');
    const mainSlides = root.querySelectorAll('[data-hpn-nad3-extra-strength-buy-box-gal-main-slide]');
    const thumbs = root.querySelectorAll('[data-hpn-nad3-extra-strength-buy-box-thumb]');
    const thumbViewport = root.querySelector('[data-hpn-nad3-extra-strength-buy-box-thumb-viewport]');
    const thumbList = root.querySelector('[data-hpn-nad3-extra-strength-buy-box-thumb-list]');
    const mainPrev = root.querySelector('[data-hpn-nad3-extra-strength-buy-box-main-nav="prev"]');
    const mainNext = root.querySelector('[data-hpn-nad3-extra-strength-buy-box-main-nav="next"]');
    // If thumbs exist, move the main-nav buttons down to sit beside the thumbs row (CSS handles positioning)
    if (root.querySelector('.hpn-nad3-extra-strength-buy-box__galThumbs')) {
      root.classList.add('hpn-nad3-extra-strength-buy-box-has-thumbs');
    } else {
      root.classList.remove('hpn-nad3-extra-strength-buy-box-has-thumbs');
    }

    if (gallery && mainSlides.length && thumbs.length) {
      let index = 0;

      const syncMainNav = () => {
        if (!mainPrev || !mainNext) return;

        const max = Math.max(0, mainSlides.length - 1);

        mainPrev.disabled = index <= 0;
        mainNext.disabled = index >= max;
      };

      const syncVideoFrameFit = (video) => {
        if (!video) return;

        const applyFit = () => {
          const videoWidth = Number(video.videoWidth || 0);
          const videoHeight = Number(video.videoHeight || 0);

          video.classList.remove('is-portrait', 'is-landscape', 'is-square', 'is-video-size-pending');

          if (!videoWidth || !videoHeight) {
            video.classList.add('is-video-size-pending');
            return;
          }

          if (videoHeight > videoWidth) {
            video.classList.add('is-portrait');
          } else if (videoWidth > videoHeight) {
            video.classList.add('is-landscape');
          } else {
            video.classList.add('is-square');
          }
        };

        applyFit();

        if (video.__hpnNad3ExtraStrengthBuyBoxFrameFitBound) return;
        video.__hpnNad3ExtraStrengthBuyBoxFrameFitBound = true;

        video.addEventListener('loadedmetadata', applyFit);
        video.addEventListener('loadeddata', applyFit);
        video.addEventListener('resize', applyFit);
      };

      const preloadGalleryAssets = () => {
        mainSlides.forEach((slide) => {
          slide.querySelectorAll('img').forEach((img) => {
            try {
              img.loading = 'eager';
              img.decoding = 'async';

              const preloadImg = new Image();

              if (img.sizes) {
                preloadImg.sizes = img.sizes;
              }

              if (img.srcset) {
                preloadImg.srcset = img.srcset;
              }

              preloadImg.src = img.currentSrc || img.src;

              if (typeof preloadImg.decode === 'function') {
                preloadImg.decode().catch(() => {});
              }
            } catch (err) {}
          });

          slide.querySelectorAll('video').forEach((video) => {
            try {
              video.preload = 'auto';
              video.setAttribute('playsinline', '');
              syncVideoFrameFit(video);
              video.load();
            } catch (err) {}
          });
        });
      };

      const pauseSlideVideos = (slide) => {
        if (!slide) return;

        slide.querySelectorAll('video').forEach((video) => {
          try {
            video.pause();
          } catch (err) {}
        });
      };

      const playSlideVideos = (slide) => {
        if (!slide) return;

        slide.querySelectorAll('video').forEach((video) => {
          try {
            syncVideoFrameFit(video);
            video.removeAttribute('muted');
            video.muted = false;
            video.setAttribute('playsinline', '');
            video.play().catch(() => {});
          } catch (err) {}
        });
      };

      const setActive = (nextIndex) => {
        const max = Math.max(0, mainSlides.length - 1);

        index = Math.min(Math.max(nextIndex, 0), max);

        mainSlides.forEach((s, i) => {
          const isActive = i === index;

          s.setAttribute('aria-hidden', isActive ? 'false' : 'true');

          if (isActive) {
            s.setAttribute('data-hpn-nad3-extra-strength-buy-box-active', '1');
          } else {
            s.removeAttribute('data-hpn-nad3-extra-strength-buy-box-active');
            pauseSlideVideos(s);
          }
        });

        thumbs.forEach((t, i) => {
          const isActive = i === index;

          t.setAttribute('aria-selected', isActive ? 'true' : 'false');

          if (isActive) {
            t.setAttribute('data-hpn-nad3-extra-strength-buy-box-active', '1');
          } else {
            t.removeAttribute('data-hpn-nad3-extra-strength-buy-box-active');
          }
        });

        const activeThumb = thumbs[index];

        if (thumbViewport && activeThumb) {
          const viewportRect = thumbViewport.getBoundingClientRect();
          const thumbRect = activeThumb.getBoundingClientRect();
          const targetLeft =
            thumbViewport.scrollLeft +
            (thumbRect.left - viewportRect.left) -
            viewportRect.width / 2 +
            thumbRect.width / 2;

          thumbViewport.scrollTo({
            left: Math.max(0, targetLeft),
            behavior: 'smooth',
          });
        }

        window.requestAnimationFrame(() => {
          playSlideVideos(mainSlides[index]);
        });

        syncMainNav();
      };

      preloadGalleryAssets();

      thumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const i = Number(t.getAttribute('data-hpn-nad3-extra-strength-buy-box-gal-index') || 0);
          setActive(i);
        });
      });

      if (mainPrev) {
        mainPrev.addEventListener('click', (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          setActive(index - 1);
        });
      }

      if (mainNext) {
        mainNext.addEventListener('click', (e) => {
          if (e && typeof e.preventDefault === 'function') e.preventDefault();
          setActive(index + 1);
        });
      }

      if (thumbViewport) {
        thumbViewport.style.scrollBehavior = 'smooth';
      }

      setActive(index);
    }
  }

  function getSyncRole(root) {
    return root
      ? (
          root.getAttribute(
            'data-hpn-nad3-extra-strength-buy-box-sync-role'
          ) || 'standalone'
        ).trim()
      : 'standalone';
  }

  function getSyncGroup(root) {
    return root
      ? (
          root.getAttribute(
            'data-hpn-nad3-extra-strength-buy-box-sync-group'
          ) || ''
        ).trim()
      : '';
  }

  function getSectionWrapper(root) {
    if (!root || typeof root.closest !== 'function') return null;

    return root.closest('[id^="shopify-section-"]');
  }

  function getSectionId(root, wrapper) {
    const configuredId = root
      ? (
          root.getAttribute(
            'data-hpn-nad3-extra-strength-buy-box-section-id'
          ) || ''
        ).trim()
      : '';

    if (configuredId) return configuredId;
    if (!wrapper || !wrapper.id) return '';

    return wrapper.id.replace(/^shopify-section-/, '');
  }

  function removeCopiedEditorBlockAttributes(root) {
    if (!root) return;

    root
      .querySelectorAll('[data-shopify-editor-block]')
      .forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
  }

  function revealSyncCopy(root) {
    if (!root) return;

    root.style.removeProperty('visibility');
    root.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-sync-ready',
      'true'
    );
  }

  function syncWrapperFromMaster(masterRoot, copyRoot) {
    if (!masterRoot || !copyRoot || masterRoot === copyRoot) {
      return copyRoot;
    }

    const masterWrapper = getSectionWrapper(masterRoot);
    const copyWrapper = getSectionWrapper(copyRoot);

    if (!masterWrapper || !copyWrapper) {
      revealSyncCopy(copyRoot);
      return copyRoot;
    }

    const masterSectionId = getSectionId(
      masterRoot,
      masterWrapper
    );
    const copySectionId = getSectionId(copyRoot, copyWrapper);

    if (!masterSectionId || !copySectionId) {
      revealSyncCopy(copyRoot);
      return copyRoot;
    }

    const copyAnchorId = copyRoot.getAttribute('id') || '';
    const copySyncGroup = getSyncGroup(copyRoot);
    const masterUsesWebkitFallback = masterWrapper.classList.contains(
      'hpn-nad3-extra-strength-buy-box--webkit-filter-fallback'
    );

  const masterMarkupForCopy = masterWrapper.cloneNode(true);

  masterMarkupForCopy
    .querySelectorAll('[data-hpn-nad3-extra-strength-buy-box-clinician-ai]')
    .forEach((widget) => widget.remove());

  const syncedMarkup = masterMarkupForCopy.innerHTML
    .split(masterSectionId)
    .join(copySectionId);

  copyWrapper.innerHTML = syncedMarkup;

    copyWrapper.classList.toggle(
      'hpn-nad3-extra-strength-buy-box--webkit-filter-fallback',
      masterUsesWebkitFallback
    );

    const syncedRoot = copyWrapper.querySelector(SELECTOR_ROOT);

    if (!syncedRoot) return null;

    syncedRoot.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-section-id',
      copySectionId
    );
    syncedRoot.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-sync-role',
      'copy'
    );
    syncedRoot.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-sync-group',
      copySyncGroup
    );
    syncedRoot.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-sync-ready',
      'true'
    );

    // A Copy never inherits or renders the Master's sticky CTA settings.
    syncedRoot.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-sticky-always-visible',
      '0'
    );
    syncedRoot.setAttribute(
      'data-hpn-nad3-extra-strength-buy-box-sticky-only-below',
      '0'
    );

    const copiedSticky = syncedRoot.querySelector(SELECTOR_STICKY);
    if (copiedSticky) {
      copiedSticky.remove();
    }

    syncedRoot.__hpnNad3ExtraStrengthBuyBoxStickyWantsOn = false;

    // Anchor IDs must stay unique on the page, so a Copy keeps its own.
    if (copyAnchorId) {
      syncedRoot.setAttribute('id', copyAnchorId);
    } else {
      syncedRoot.removeAttribute('id');
    }

    syncedRoot.classList.remove(
      'hpn-nad3-extra-strength-buy-box-sticky-active'
    );

    removeCopiedEditorBlockAttributes(syncedRoot);
    revealSyncCopy(syncedRoot);

    return syncedRoot;
  }

  function syncBuyBoxCopies() {
    const roots = Array.from(
      document.querySelectorAll(SELECTOR_ROOT)
    );
    const mastersByGroup = new Map();

    roots.forEach((root) => {
      if (getSyncRole(root) !== 'source') return;

      const group = getSyncGroup(root);
      if (!group) return;

      if (mastersByGroup.has(group)) {
        console.warn(
          `HPN NAD3 Buy Box found more than one Master in sync group "${group}". The first Master on the page will be used.`
        );
        return;
      }

      mastersByGroup.set(group, root);
    });

    roots.forEach((root) => {
      if (getSyncRole(root) !== 'copy') return;

      const group = getSyncGroup(root);
      const master = group ? mastersByGroup.get(group) : null;

      if (!master) {
        revealSyncCopy(root);

        if (
          !root.__hpnNad3ExtraStrengthBuyBoxMissingMasterWarned
        ) {
          const missingGroupName = group || '(blank)';

          console.warn(
            `HPN NAD3 Buy Box Copy could not find a Master in sync group "${missingGroupName}". Its own section settings will be used instead.`
          );

          root.__hpnNad3ExtraStrengthBuyBoxMissingMasterWarned =
            true;
        }

        return;
      }

      syncWrapperFromMaster(master, root);
    });
  }

  function initBuyBoxLocationMessages() {
    const hasBuyBoxLocationMessage = Array.from(
      document.querySelectorAll(SELECTOR_ROOT)
    ).some((root) => {
      return !!root.querySelector('[data-location-type]');
    });

    if (!hasBuyBoxLocationMessage) return;

    if (
      window.themeLocationMessage &&
      typeof window.themeLocationMessage.init === 'function'
    ) {
      window.themeLocationMessage.init();
    }
  }

  function bindCliniciansChoiceResponsivePlacement(root) {
    if (!root) return;

    const widget = root.querySelector('#frontrow-badge');
    const bulletsNote = root.querySelector(
      '.hpn-nad3-extra-strength-buy-box__bulletsNote'
    );

    if (!widget || !bulletsNote || !widget.parentNode) return;

    if (widget.__hpnNad3ExtraStrengthCliniciansChoiceSync) {
      widget.__hpnNad3ExtraStrengthCliniciansChoiceSync();
      return;
    }

    const originMarker = document.createComment(
      'hpn-nad3-extra-strength-buy-box-clinicians-choice-origin'
    );

    widget.parentNode.insertBefore(originMarker, widget);

    const mobileQuery = window.matchMedia('(max-width: 899px)');

    const syncPlacement = () => {
      if (mobileQuery.matches) {
        if (widget.previousElementSibling !== bulletsNote) {
          bulletsNote.insertAdjacentElement('afterend', widget);
        }

        widget.classList.add(
          'hpn-nad3-extra-strength-buy-box__cliniciansChoice--mobile'
        );
      } else {
        const originParent = originMarker.parentNode;

        if (originParent && widget.previousSibling !== originMarker) {
          originParent.insertBefore(widget, originMarker.nextSibling);
        }

        widget.classList.remove(
          'hpn-nad3-extra-strength-buy-box__cliniciansChoice--mobile'
        );
      }
    };

    widget.__hpnNad3ExtraStrengthCliniciansChoiceSync = syncPlacement;

    syncPlacement();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncPlacement);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(syncPlacement);
    }
  }

  function init() {
    syncBuyBoxCopies();

    document.querySelectorAll(SELECTOR_ROOT).forEach((root) => {
      bindRoot(root);
      bindCliniciansChoiceResponsivePlacement(root);
    });

    initBuyBoxLocationMessages();
  }

  if (window.__hpnNad3ExtraStrengthBuyBoxRuntimeInitialized) {
    return;
  }

  window.__hpnNad3ExtraStrengthBuyBoxRuntimeInitialized = true;

  const scheduleInit = () => {
    window.requestAnimationFrame(init);
  };

  document.addEventListener(
    'shopify:section:load',
    scheduleInit
  );
  document.addEventListener(
    'shopify:section:reorder',
    scheduleInit
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();
